import {
	addDays,
	addMonths,
	addWeeks,
	endOfMonth,
	endOfYear,
	format,
	getMonth,
	getYear,
	isAfter,
	isBefore,
	isSameDay,
	isSameMonth,
	isSameWeek,
	isValid,
	setMonth,
	setYear,
	startOfMonth,
	startOfWeek,
	startOfYear
} from 'date-fns'
import { useMemo } from 'react'
import { z } from 'zod'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { Table } from '@/components/ui/table'
import { parseDate } from '@/dates'
import { useGetVisualVettingVolume } from '@/features/luxe-submissions/queries/use-get-visual-vetting-volume'
import type { VisualVettingVolumeResponse } from '@/features/luxe-submissions/types'
import { cn } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { useTranslation } from '@/translations/i18n'

const TABS = ['daily', 'weekly', 'monthly'] as const
type Tab = (typeof TABS)[number]

const TeamProductivitySearchParamsSchema = z.object({
	tab: z.enum(TABS).optional(),
	month: z.string().optional(),
	year: z.string().optional()
})

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, idx) => ({
	value: idx + 1,
	label: format(setMonth(new Date(), idx), 'MMMM')
}))

const formatBucketHeader = (value: string, tab: Tab) => {
	const d = parseDate(value)
	if (!isValid(d)) {
		return value
	}
	if (tab === 'monthly') {
		return format(d, 'MMM yyyy')
	}
	if (tab === 'weekly') {
		return `${format(d, 'MMM d')} - ${format(addDays(d, 6), 'MMM d')}`
	}
	return format(d, 'MMM d')
}

const formatMember = (row: VisualVettingVolumeResponse['rows'][number]) => {
	const fullName = [row.firstName, row.lastName].filter(Boolean).join(' ')
	return fullName || row.email
}

const SKELETON_ROW_COUNT = 8

const BUCKET_COLUMN_WIDTH: Record<Tab, number> = {
	daily: 96,
	weekly: 144,
	monthly: 160
}

const computePlaceholderBuckets = (
	tab: Tab,
	from: string,
	to: string
): string[] => {
	const start = parseDate(from)
	const end = parseDate(to)
	const buckets: string[] = []

	if (tab === 'monthly') {
		let cur = startOfMonth(start)
		while (isSameMonth(cur, end) || isBefore(cur, end)) {
			buckets.push(format(cur, 'yyyy-MM-dd'))
			cur = addMonths(cur, 1)
		}
		return buckets
	}

	if (tab === 'weekly') {
		let cur = startOfWeek(start, { weekStartsOn: 1 })
		while (
			isSameWeek(cur, end, { weekStartsOn: 1 }) ||
			isBefore(cur, end)
		) {
			buckets.push(format(cur, 'yyyy-MM-dd'))
			cur = addWeeks(cur, 1)
		}
		return buckets
	}

	let cur = start
	while (isSameDay(cur, end) || isBefore(cur, end)) {
		buckets.push(format(cur, 'yyyy-MM-dd'))
		cur = addDays(cur, 1)
	}
	return buckets
}

interface ProductivityTableProps {
	data?: VisualVettingVolumeResponse
	isLoading: boolean
	tab: Tab
	from: string
	to: string
	totalLabel: string
	memberLabel: string
}

function ProductivityTable({
	data,
	isLoading,
	tab,
	from,
	to,
	totalLabel,
	memberLabel
}: ProductivityTableProps) {
	const buckets = data?.buckets ?? computePlaceholderBuckets(tab, from, to)
	const rows = data?.rows ?? []
	const totalsByBucket = data?.totalsByBucket ?? []
	const grandTotal = data?.grandTotal ?? 0

	return (
		<div className='flex h-full w-full flex-col overflow-auto rounded-md border border-neutral-200'>
			<Table.Root style={{ minWidth: '100%' }}>
				<Table.Header>
					<Table.HeaderRow>
						<Table.HeaderCell
							className='sticky top-0 z-3 rounded-bl-none! shadow-[inset_-1px_0_0_var(--color-neutral-200),inset_0_-1px_0_var(--color-neutral-200)]'
							pinningPosition='left'
							style={{ width: 260 }}
						>
							<Table.HeaderCellLabel>
								{memberLabel}
							</Table.HeaderCellLabel>
						</Table.HeaderCell>
						{buckets.map((bucket) => (
							<Table.HeaderCell
								className='sticky top-0 z-2 whitespace-nowrap border-neutral-200 border-r shadow-[inset_0_-1px_0_var(--color-neutral-200)]'
								key={bucket}
								style={{ width: BUCKET_COLUMN_WIDTH[tab] }}
							>
								<Table.HeaderCellLabel className='mx-auto'>
									{formatBucketHeader(bucket, tab)}
								</Table.HeaderCellLabel>
							</Table.HeaderCell>
						))}
						<Table.HeaderCell
							className='sticky top-0 z-3 rounded-br-none! shadow-[inset_1px_0_0_var(--color-neutral-200),inset_0_-1px_0_var(--color-neutral-200)]'
							pinningPosition='right'
							style={{ width: 120 }}
						>
							<Table.HeaderCellLabel className='mx-auto'>
								{totalLabel}
							</Table.HeaderCellLabel>
						</Table.HeaderCell>
					</Table.HeaderRow>
				</Table.Header>
				<Table.Body>
					{isLoading
						? Array.from({ length: SKELETON_ROW_COUNT }).map(
								(_, rowIdx) => (
									<Table.Row
										// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholder rows
										key={`skeleton-${rowIdx}`}
									>
										<Table.Cell
											className='h-12 shadow-[inset_-1px_0_0_var(--color-neutral-200)]'
											pinningPosition='left'
										>
											<Table.CellSkeleton />
										</Table.Cell>
										{buckets.map((bucket) => (
											<Table.Cell
												className='h-12 border-neutral-200 border-r'
												key={bucket}
											>
												<Table.CellSkeleton />
											</Table.Cell>
										))}
										<Table.Cell
											className='h-12 shadow-[inset_1px_0_0_var(--color-neutral-200)]'
											pinningPosition='right'
										>
											<Table.CellSkeleton />
										</Table.Cell>
									</Table.Row>
								)
							)
						: rows.map((row) => (
								<Table.Row key={row.userId}>
									<Table.Cell
										className='h-12 font-medium text-neutral-900 shadow-[inset_-1px_0_0_var(--color-neutral-200)]'
										pinningPosition='left'
									>
										{formatMember(row)}
									</Table.Cell>
									{row.values.map((value, idx) => (
										<Table.Cell
											className={cn(
												'h-12 border-neutral-200 border-r text-center tabular-nums',
												value === 0
													? 'text-neutral-300'
													: 'text-neutral-700'
											)}
											key={`${row.userId}-${idx}`}
										>
											{value === 0
												? '–'
												: value.toLocaleString()}
										</Table.Cell>
									))}
									<Table.Cell
										className='h-12 text-center font-medium text-neutral-900 tabular-nums shadow-[inset_1px_0_0_var(--color-neutral-200)]'
										pinningPosition='right'
									>
										{row.total.toLocaleString()}
									</Table.Cell>
								</Table.Row>
							))}
					{!isLoading && (
						<Table.Row>
							<Table.Cell
								className='sticky bottom-0 z-3 h-12 font-medium text-neutral-900 uppercase shadow-[inset_-1px_0_0_var(--color-neutral-200),inset_0_1px_0_var(--color-neutral-200)]'
								pinningPosition='left'
							>
								{totalLabel}
							</Table.Cell>
							{totalsByBucket.map((value, idx) => (
								<Table.Cell
									className='sticky bottom-0 z-2 h-12 border-neutral-200 border-r text-center font-medium text-neutral-900 tabular-nums shadow-[inset_0_1px_0_var(--color-neutral-200)]'
									// biome-ignore lint/suspicious/noArrayIndexKey: positional total cells
									key={`total-${idx}`}
								>
									{value === 0 ? '–' : value.toLocaleString()}
								</Table.Cell>
							))}
							<Table.Cell
								className='sticky right-0 bottom-0 z-3 h-12 text-center font-semibold text-neutral-900 tabular-nums shadow-[inset_1px_0_0_var(--color-neutral-200),inset_0_1px_0_var(--color-neutral-200)]'
								pinningPosition='right'
							>
								{grandTotal.toLocaleString()}
							</Table.Cell>
						</Table.Row>
					)}
				</Table.Body>
			</Table.Root>
		</div>
	)
}

function LuxeSubmissionsTeamProductivity() {
	const { t } = useTranslation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		TeamProductivitySearchParamsSchema
	)

	const activeTab: Tab = searchParams.tab ?? 'daily'

	const yearOptions = useMemo(() => {
		const currentYear = getYear(new Date())
		const start = 2026
		const end = Math.max(currentYear, start)
		const years: number[] = []
		for (let y = start; y <= end; y++) {
			years.push(y)
		}
		return years
	}, [])

	const now = new Date()
	const defaultYear = getYear(now)
	const defaultMonth = getMonth(now) + 1

	const parsedYear = Number(searchParams.year)
	const selectedYear = yearOptions.includes(parsedYear)
		? parsedYear
		: defaultYear

	const parsedMonth = Number(searchParams.month)
	const isMonthInRange = parsedMonth >= 1 && parsedMonth <= 12
	const isMonthInFuture =
		isMonthInRange &&
		isAfter(
			startOfMonth(setMonth(setYear(now, selectedYear), parsedMonth - 1)),
			startOfMonth(now)
		)
	const selectedMonth =
		isMonthInRange && !isMonthInFuture ? parsedMonth : defaultMonth

	const monthValue = String(selectedMonth)
	const yearValue = String(selectedYear)

	const range = useMemo(() => {
		const today = new Date()
		if (activeTab === 'monthly') {
			const yearStart = startOfYear(setYear(today, selectedYear))
			const yearEnd = endOfYear(setYear(today, selectedYear))
			const to = isAfter(yearEnd, today) ? today : yearEnd
			return {
				from: format(yearStart, 'yyyy-MM-dd'),
				to: format(to, 'yyyy-MM-dd')
			}
		}
		const selected = setMonth(
			setYear(today, selectedYear),
			selectedMonth - 1
		)
		const monthStart = startOfMonth(selected)
		const monthEnd = endOfMonth(selected)
		const to = isAfter(monthEnd, today) ? today : monthEnd

		return {
			from: format(monthStart, 'yyyy-MM-dd'),
			to: format(to, 'yyyy-MM-dd')
		}
	}, [activeTab, selectedMonth, selectedYear])

	const { data, isLoading, isError } = useGetVisualVettingVolume({
		granularity: activeTab,
		from: range.from,
		to: range.to
	})

	return (
		<div className='flex flex-1 flex-col overflow-hidden'>
			<div className='flex items-center justify-between border-neutral-200 border-b px-10 pt-6 pb-6'>
				<h1 className='font-medium text-lg text-neutral-900'>
					{t('luxe_submissions:team_productivity_title')}
				</h1>
			</div>

			<div className='flex items-end justify-between gap-4 border-neutral-300 border-b px-10 pt-6'>
				<div className='flex items-center gap-1'>
					{TABS.map((tab) => (
						<button
							className={cn(
								'flex h-9 items-center gap-2 border-b-2 px-1 pb-3 font-medium text-base',
								activeTab === tab
									? 'border-neutral-700 text-neutral-700'
									: 'cursor-pointer border-transparent text-neutral-500 opacity-55 hover:opacity-80'
							)}
							key={tab}
							onClick={() =>
								setSearchParams({
									tab: tab === 'daily' ? undefined : tab
								})
							}
							type='button'
						>
							{t(`luxe_submissions:team_productivity_tab_${tab}`)}
						</button>
					))}
				</div>

				<div className='flex items-center gap-2 pb-3'>
					{activeTab !== 'monthly' && (
						<Select
							onValueChange={(value: string | null) => {
								if (value) {
									setSearchParams({ month: value })
								}
							}}
							value={monthValue}
						>
							<SelectTrigger className='w-36' size='sm'>
								<SelectValue>
									{MONTH_OPTIONS.find(
										(option) =>
											option.value === selectedMonth
									)?.label ?? ''}
								</SelectValue>
							</SelectTrigger>
							<SelectContent className='w-(--anchor-width)'>
								{MONTH_OPTIONS.map((option) => (
									<SelectItem
										disabled={isAfter(
											startOfMonth(
												setMonth(
													setYear(now, selectedYear),
													option.value - 1
												)
											),
											startOfMonth(now)
										)}
										key={option.value}
										value={String(option.value)}
									>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}

					<Select
						onValueChange={(value: string | null) => {
							if (value) {
								setSearchParams({ year: value })
							}
						}}
						value={yearValue}
					>
						<SelectTrigger className='w-36' size='sm'>
							<SelectValue>{yearValue}</SelectValue>
						</SelectTrigger>
						<SelectContent className='w-(--anchor-width)'>
							{yearOptions.map((year) => (
								<SelectItem key={year} value={String(year)}>
									{year}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			<div className='flex flex-1 flex-col overflow-hidden px-10 py-6'>
				{isError ? (
					<div className='flex flex-1 items-center justify-center text-neutral-500 text-sm'>
						{t('common:error_table_title')}
					</div>
				) : (
					<ProductivityTable
						data={data}
						from={range.from}
						isLoading={isLoading}
						memberLabel={t(
							'luxe_submissions:team_productivity_member'
						)}
						tab={activeTab}
						to={range.to}
						totalLabel={t(
							'luxe_submissions:team_productivity_total'
						)}
					/>
				)}
			</div>
		</div>
	)
}

export { LuxeSubmissionsTeamProductivity }
